import { useNavigate } from 'react-router-dom'
import type { createBrowserRouter } from 'react-router-dom'

/** Taken from react-router rather than @remix-run/router, which is transitive. */
type Router = ReturnType<typeof createBrowserRouter>

/**
 * react-router's own position in the history stack: the `idx` it writes into
 * `history.state` on every entry. It survives a reload, because the browser
 * keeps each entry's state.
 */
export const historyIndex = (): number => {
    const state = window.history.state as { idx?: number } | null
    return typeof state?.idx === 'number' ? state.idx : 0
}

/**
 * The pathname at each history index this document has visited, from
 * `bootIndex` up. Everything below `bootIndex` belongs to the document before
 * a reload, or before a bank gateway: going back to it is a full page load,
 * not a route change, so nothing here ever pops that far.
 */
const paths = new Map<number, string>()
let bootIndex = 0

/** Installed once, on the app's router; `useFinishFlow` reads what it records. */
export const trackHistoryPaths = (router: Router) => {
    paths.clear()
    bootIndex = historyIndex()
    const record = () => {
        if (router.state.navigation.state !== 'idle') return
        paths.set(historyIndex(), router.state.location.pathname)
    }
    record()
    router.subscribe(record)
}

const pathnameOf = (to: string) => to.split(/[?#]/)[0]

/**
 * Every multi-screen flow in the app, as the pathnames of its screens. Kept in
 * one list so that the next flow is added here rather than forgotten.
 */
export const FLOWS = {
    /** «درخواست برداشت» — every step, with or without a fund in the path */
    withdrawRequest: /^\/fund\/withdraw-request(\/\d+)?\/?$/,
    /** «واریز وجه» — the form, then the receipt */
    deposit: /^\/loans\/(deposit\/\d+|transaction)\/?$/,
    /** «پرداخت برای دیگری» */
    payForOther: /^\/easy-pay\/pay-for-other\/\d+\/?$/,
    /** the easy-pay basket and its receipt */
    easyPay: /^\/easy-pay(\/init-new)?\/?$/,
    /** «اعطای وام» — the wizard */
    grantLoan: /^\/loans\/management\/grant\/\d+\/?$/,
    /** a new loan the old way — accepting a request, then the instalments */
    newLoan: /^\/loans\/((accept\/)?management\/add-new\/[^/]+\/[^/]+|add-new\/management\/final(\/add)?)\/?$/,
    /** «ثبت وام اعطا شده در گذشته» */
    pastLoan: /^\/loans\/(management\/add-old\/[^/]+\/[^/]+|add-old\/management\/(installments|final\/add))\/?$/,
    /** «برداشت وجه» — the form, then its confirmation */
    cashout: /^\/loans\/withDraw\/([^/]+\/[^/]+|final)\/?$/,
    /** «ساخت صندوق», every page of it */
    createFund: /^\/add-box(\/|$)/,
    /** buying a plan: the choice, then subscription or commission */
    fundPlan: /^\/fund\/plans\//,
    /** «صندوقچه»: the box-type chooser, then the create form */
    cashboxCreate: /^\/(add-box|cashbox\/create)\/?$/,
    /** the members screen, as the place a box is activated from */
    cashboxActivate: /^\/cashbox\/\d+\/members\/?$/,
    /** قرعه‌کشی, then paying its winners */
    cashboxDraw: /^\/cashbox\/\d+\/(draw|pay-winners)\/?$/,
    cashboxClaim: /^\/cashbox\/\d+\/claim\/?$/,
    cashboxManualCashout: /^\/cashbox\/\d+\/manual-cashout\/?$/,
    cashboxPlan: /^\/cashbox\/\d+\/plans\/?$/,
    cashboxShareGroup: /^\/cashbox\/\d+\/share-group(\/\d+)?\/?$/,
    cashboxSettings: /^\/cashbox\/\d+\/settings\/?$/,
}

/** every screen of one صندوقچه — all of them are dead once it is deleted */
export const cashboxScreens = (id: string | number) =>
    new RegExp(`^/cashbox/${id}(/|$)`)

/**
 * Leave a flow that has just succeeded, so that back never walks into it.
 *
 * THE RULE: back, from the screen a finished flow lands on, goes to wherever
 * the flow was entered from — never into the flow itself. Its screens are
 * spent: they still hold what was typed into them, and walking back onto them
 * offers to register the same payment or file the same request again. Every
 * flow used to end in a plain `navigate(result)` (or a `replace` of its last
 * screen only), which left all of its screens under the result: «واریز وجه»
 * then back reopened the receipt screen; a filed «درخواست برداشت» then back
 * reopened the wizard's last step.
 *
 * `within` matches the pathname of every screen that belongs to the flow.
 * `finish(to)` finds the run of those screens directly beneath the current
 * one, and then:
 *
 * - if the page the flow was entered from IS `to`, goes straight back to it —
 *   «واریز وجه» from a fund page ends on that fund page, so the entry that is
 *   already there is reused rather than a second copy stacked on top of it;
 * - otherwise pops the flow's screens and puts `to` in place of the first, so
 *   back from `to` reaches the page the flow was entered from.
 *
 * Any new flow that ends by navigating somewhere should end through this.
 *
 * A reload mid-flow makes the screens before it unreachable to a route
 * change; those stay behind, which is what every flow did before this.
 */
export default function useFinishFlow(within: RegExp) {
    const navigate = useNavigate()

    return (to: string, state?: unknown) => {
        const here = historyIndex()
        let start = here
        while (start > bootIndex && within.test(paths.get(start - 1) ?? '')) {
            start -= 1
        }
        const pushed = here - start

        if (start > bootIndex && paths.get(start - 1) === pathnameOf(to)) {
            navigate(-(pushed + 1))
            return
        }
        if (pushed === 0) {
            navigate(to, { replace: true, state })
            return
        }
        // The replace has to land on the flow's first screen, so it waits for
        // the pop to get there.
        window.addEventListener(
            'popstate',
            () => {
                if (historyIndex() === start) {
                    navigate(to, { replace: true, state })
                }
            },
            { once: true }
        )
        navigate(-pushed)
    }
}
